var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React, { isValidElement, PureComponent } from 'react';
import { DismissStyle, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { EditorContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, eventListener, expression, number, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { AbortableCloseEvent, CloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, isEnumValid, isFunction } from '@philips-internal/oswald-ui-utils';
import { Button } from '../button/button.js';
import { Link } from '../link/link.js';
import ToastNotificationAPI from './toast-notification-api.js';
import './toast-notification.css';
const DEFAULT_TIMEOUT = 5000;
const ToastNotificationContainer = {
    acceptedTypes: [
        ComponentType.NAVIGATION,
    ],
    name: '',
};
let ToastNotification = class ToastNotification extends PureComponent {
    constructor() {
        super(...arguments);
        this.closeToast = () => {
            const { onBeforeClose, onClose } = this.props;
            let close = true;
            const abort = () => { close = false; };
            if (isFunction(onBeforeClose)) {
                onBeforeClose(new AbortableCloseEvent({ abort }));
            }
            if (close && isFunction(onClose)) {
                onClose(new CloseEvent());
            }
        };
    }
    componentDidMount() {
        const { dismissible, timeout } = this.props;
        if (dismissible && !timeout) {
            return;
        }
        this.timeoutTimer = setTimeout(this.closeToast, timeout ?? DEFAULT_TIMEOUT);
    }
    componentWillUnmount() {
        if (this.timeoutTimer) {
            clearTimeout(this.timeoutTimer);
        }
    }
    renderCloseButton() {
        const { children, dismissible } = this.props;
        const element = Array.isArray(children) ? children[0] : children;
        if (isValidElement(element) || !dismissible) {
            return null;
        }
        return (React.createElement(Link, { className: "c-toast-notification__close", onClick: this.closeToast }));
    }
    renderMessage() {
        const { dismissible, message, title, type } = this.props;
        const isBannerTitle = type === ToastType.BANNER && title;
        const classes = classNames({
            'c-toast-notification__dismissable': Boolean(dismissible),
            'c-toast-notification__message': true,
            'c-toast-notification__message--with-title': Boolean(isBannerTitle),
        });
        return (React.createElement("div", { className: classes }, MessageService.translate(message)));
    }
    renderTitle() {
        const { showIcon, title } = this.props;
        const classes = classNames({
            'c-toast-notification__title-bar': true,
            'c-toast-notification__title-bar--with-title': Boolean(title),
        });
        return (React.createElement("div", { className: classes },
            showIcon ? React.createElement("div", { className: "c-toast-notification__icon" }) : null,
            title ? React.createElement("div", { className: "c-toast-notification__title" }, MessageService.translate(title)) : null));
    }
    renderChildren() {
        const { children, uuid } = this.props;
        const element = Array.isArray(children) ? children[0] : children;
        if (!element && !this.context?.isDesigning()) {
            return null;
        }
        return (React.createElement(Container, { acceptedTypes: ToastNotificationContainer.acceptedTypes, className: "c-toast-notification__actions u-expand", uuid: uuid },
            children,
            this.renderDismissAction()));
    }
    renderDismissAction() {
        const { dismissible, dismissExpression, dismissStyle } = this.props;
        if (!dismissible) {
            return null;
        }
        const DismissComponent = dismissStyle === DismissStyle.BUTTON ? Button : Link;
        return (React.createElement(DismissComponent, { className: "c-toast-notification__dismiss", onClick: this.closeToast, value: dismissExpression ?? '' }));
    }
    renderMessageWrapper() {
        const { title } = this.props;
        const classes = classNames({
            'c-toast-notification__message-wrapper': true,
            'c-toast-notification__message-wrapper--with-title': Boolean(title),
        });
        return (React.createElement("div", { className: classes },
            this.renderTitle(),
            this.renderMessage()));
    }
    renderContentWrapper() {
        return (React.createElement("div", { className: "c-toast-notification__content-wrapper" },
            this.renderMessageWrapper(),
            this.renderCloseButton()));
    }
    render() {
        const { className, highlight, inline, level, title, type, visible } = this.props;
        if (!visible) {
            return null;
        }
        const classes = classNames({
            'c-toast-notification': true,
            'c-toast-notification--highlighted': Boolean(highlight),
            [`c-toast-notification--${level}`]: isEnumValid(ToastLevel, level),
            [`c-toast-notification--${type}`]: isEnumValid(ToastType, type),
            [`c-toast-notification--${type}--without-title`]: Boolean(!title),
            [`${className}`]: Boolean(className),
            'c-toast-notification--inline': Boolean(inline),
        });
        return (React.createElement("div", { className: classes },
            this.renderContentWrapper(),
            this.renderChildren()));
    }
};
ToastNotification.contextType = EditorContext;
ToastNotification.propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    dismissExpression: expression,
    dismissible: bool,
    dismissStyle: oneOf(enumValues(DismissStyle)),
    highlight: bool,
    inline: bool,
    level: oneOf(enumValues(ToastLevel)),
    message: expression,
    onBeforeClose: eventListener,
    onClose: eventListener,
    showIcon: bool,
    timeout: number,
    title: expression,
    type: oneOf(enumValues(ToastType)),
    uuid: string,
    visible: bool,
};
ToastNotification.defaultProps = {
    dismissExpression: 'tws.toast-notification.dismiss',
    dismissStyle: DismissStyle.LINK,
    highlight: false,
    level: ToastLevel.INFORMATION,
    showIcon: false,
    type: ToastType.BANNER,
    uuid: '',
    visible: true,
};
ToastNotification = __decorate([
    AwesomeComponent({ api: ToastNotificationAPI, containers: [ToastNotificationContainer], type: ComponentType.FEEDBACK })
], ToastNotification);
export { ToastNotification, };
export default ToastNotification;
//# sourceMappingURL=toast-notification.js.map